import React, { useContext, useState, useEffect } from "react";
import Select from "react-select";
import { isEmpty, startCase } from "lodash";
import AuthContext from "../context/AuthContext";
import { apiGet, apiPost } from "../Utils/apiFetch";
import { apiPath } from "../Utils/apiPath";
import { toast } from "react-toastify";
import { isMobile } from "react-device-detect";

// ---- Field configs matching the REAL API keys ----
const limitSettingFields = [
  {
    key: "exposureLimit",
    label: "Exposure Limit: ",
    placeholder: "Enter exposure limit",
  },
  {
    key: "bookmakingCommission",
    label: "Bookmaking commission: ",
    placeholder: "Enter Bookmaking commission",
  },
  {
    key: "exchangeCommission",
    label: "Exchange Commission: ",
    placeholder: "Enter Match Odds Commission",
  },
  {
    key: "betDelay",
    label: "Bet Delay [Second]: ",
    placeholder: "Enter Bet Delay",
  },
  {
    key: "minutesBeforeInPlay",
    label: "No. of Minutes Before Inplay: ",
    placeholder: "Enter No. of Minutes Before Inplay",
  },
];

const marketSettingFields = [
  {
    key: "minRate",
    label: "Bet Min Rate: ",
    placeholder: "Enter Bet Min Rate",
  },
  {
    key: "maxRate",
    label: "Bet Max Rate: ",
    placeholder: "Enter Bet Max Rate",
  },
  { key: "minStake", label: "Min Stake: ", placeholder: "Enter Min Stake" },
  { key: "maxStake", label: "Max Stake: ", placeholder: "Enter Max Stake" },
  { key: "maxProfit", label: "Max Profit: ", placeholder: "Enter Max Profit" },
  { key: "maxLoss", label: "Max Loss: ", placeholder: "Enter Max Loss" },
];

const sessionSettingFields = [
  { key: "minStake", label: "Min Stake: ", placeholder: "Enter Min Stake" },
  { key: "maxStake", label: "Max Stake: ", placeholder: "Enter Max Stake" },
  { key: "maxLoss", label: "Max Loss: ", placeholder: "Enter Max Loss" },
  { key: "maxProfit", label: "Max Profit: ", placeholder: "Enter Max Profit" },
  {
    key: "perRateMaxStake",
    label: "Per Rate Max Stake: ",
    placeholder: "Enter Per Rate Max Stake",
  },
  {
    key: "commission",
    label: "Commision(%): ",
    placeholder: "Enter commission",
  },
  { key: "betDelay", label: "Bet Delay: ", placeholder: "Enter betDelay" },
];

// shared shape for cricket / soccer / tennis bookmaking settings
const bookmakingSettingFields = [
  { key: "betDelay", label: "Bet Delay: ", placeholder: "Enter Bet Delay" },
  { key: "minStake", label: "Min Stake: ", placeholder: "Enter Min Stake" },
  { key: "maxStake", label: "Max Stake: ", placeholder: "Enter Max Stake" },
  { key: "maxProfit", label: "Max Profit: ", placeholder: "Enter Max Profit" },
  { key: "maxLoss", label: "Max Loss: ", placeholder: "Enter Max Loss" },
  {
    key: "maxStakePerRate",
    label: "Max Stake Per Rate: ",
    placeholder: "Enter Max Stake Per Rate",
  },
];

// "is" + PascalCase, matching the real API's checkbox key pattern
const checkboxKeyFor = (key) =>
  `is${key.charAt(0).toUpperCase()}${key.slice(1)}`;

const buildDefaultSection = (fields) =>
  fields.reduce((acc, f) => {
    acc[f.key] = "";
    acc[checkboxKeyFor(f.key)] = false;
    return acc;
  }, {});

const DEFAULT_VALUES = {
  limitSetting: buildDefaultSection(limitSettingFields),
  marketSetting: buildDefaultSection(marketSettingFields),
  sessionSetting: buildDefaultSection(sessionSettingFields),
  cricketBookmakingSetting: buildDefaultSection(bookmakingSettingFields),
  soccerBookmakingSetting: buildDefaultSection(bookmakingSettingFields),
  tennisBookmakingSetting: buildDefaultSection(bookmakingSettingFields),
};

const sportOptions = [
  { label: "Soccer", value: "soccer" },
  { label: "Tennis", value: "tennis" },
  { label: "Cricket", value: "cricket" },
];

const USER_WISE_OPTIONS = [
  {
    id: "senior sub admin",
    key: "senior_sub_admin",
    label: "Senior Sub Admin Wise",
  },
  { id: "sub admin", key: "sub_admin", label: "Sub Admin Wise" },
  { id: "super", key: "super", label: "Super Wise" },
  { id: "master", key: "master", label: "Master Wise" },
];

const TYPE_WISE_OPTIONS = [
  { id: "typeWiseDefault", key: "default", label: "Default" },
  { id: "sport", key: "sport", label: "Sport Wise" },
  { id: "competition", key: "competition", label: "Competition Wise" },
  { id: "event", key: "event", label: "Event Wise" },
  { id: "market", key: "market", label: "Market Wise" },
];

const Setting = () => {
  const [select1, setSelect1] = useState("default");
  const [select2, setSelect2] = useState("default");
  const [typeSelect, setTypeSelect] = useState(null);
  const [typeSelect2, setTypeSelect2] = useState(null);
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [playType, setPlayType] = useState("all"); // "all" | "cricket" | "soccer" | "tennis"
  const [list, setList] = useState([]);
  const [listCompetition, setListCompetition] = useState([]);
  const [listEvents, setListEvents] = useState([]);
  const [listMarket, setListMarket] = useState([]);
  const [settingList, setSettingList] = useState([]);
  const [settingValues, setSettingValues] = useState({
    settingTypeFirst: "default",
    settingTypeFirstId: "",
    settingTypeSecond: "default",
    settingTypeSecondId: "",
  });

  const getData = async () => {
    const { status, data: response } = await apiGet(apiPath.userList);
    if (status === 200 && response.success && response?.results?.length > 0) {
      setList(
        response.results.map((res) => ({
          label: res?.username,
          value: res?._id,
        })),
      );
    }
  };

  const getDataSetting = async () => {
    const { status, data: response } = await apiGet(apiPath.settingList);
    if (status === 200 && response.success && response?.results?.length > 0) {
      const matched = response.results.find(
        (res) =>
          res?.settingTypeFirst === settingValues?.settingTypeFirst &&
          res?.settingTypeFirstId === settingValues?.settingTypeFirstId &&
          res?.settingTypeSecond === settingValues?.settingTypeSecond &&
          res?.settingTypeSecondId === settingValues?.settingTypeSecondId,
      );
      if (!isEmpty(matched)) setValues(matched);
      setSettingList(response.results);
    }
  };

  const getCompetition = async () => {
    const { status, data: response } = await apiGet(apiPath.matchFilterList);
    if (status === 200 && response.success && response?.results?.length > 0) {
      setListCompetition(
        response.results
          .filter(
            (res, index, self) =>
              index === self.findIndex((r) => r.seriesId === res.seriesId),
          )
          .map((res) => ({
            label: `${startCase(res?.gameType)} > ${startCase(res?.seriesName)}`,
            value: res,
          })),
      );
      setListEvents(
        response.results.map((res) => ({
          label: `${startCase(res?.gameType)} > ${startCase(res?.eventName)}`,
          value: res,
        })),
      );
      setListMarket(
        response.results.map((res) => ({
          label: `${startCase(res?.gameType)} > ${startCase(res?.eventName)} > ${startCase(res?.market)}`,
          value: res,
        })),
      );
    }
  };

  useEffect(() => {
    getData();
    getCompetition();
    getDataSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ObjSelect = {
    sport: sportOptions,
    competition: listCompetition,
    event: listEvents,
    market: listMarket,
  };

  const handleChangeSetting = (obj) => {
    const found = settingList?.find(
      (res) =>
        res?.settingTypeFirst === obj?.settingTypeFirst &&
        res?.settingTypeFirstId === obj?.settingTypeFirstId &&
        res?.settingTypeSecond === obj?.settingTypeSecond &&
        res?.settingTypeSecondId === obj?.settingTypeSecondId,
    );
    setValues(!isEmpty(found) ? found : DEFAULT_VALUES);
  };

  const handleChange1 = (value) => {
    setSettingValues((prev) => ({
      ...prev,
      settingTypeFirst: value,
      settingTypeFirstId: "",
    }));
    setPlayType("all");
    setSelect1(value);
    setTypeSelect2(null);
    setValues(DEFAULT_VALUES);
  };

  const handleChange2 = (value) => {
    setSettingValues((prev) => ({
      ...prev,
      settingTypeSecond: value,
      settingTypeSecondId: "",
    }));
    setSelect2(value);
    setTypeSelect(null);
    setValues(DEFAULT_VALUES);
  };

  const handleChangeTypeSelect2 = (e, selected) => {
    setTypeSelect2(e);
    if (["cricket", "soccer", "tennis"].includes(e?.value)) {
      setPlayType(e.value);
      setSettingValues((prev) => {
        const next = { ...prev, settingTypeFirstId: e.value };
        handleChangeSetting(next);
        return next;
      });
    } else {
      setPlayType(e?.value?.gameType || "all");
      setSettingValues((prev) => {
        const next = {
          ...prev,
          settingTypeFirstId:
            selected === "market"
              ? e?.value?.marketId
              : selected === "competition"
                ? e?.value?.seriesId
                : e?.value?.eventId,
        };
        handleChangeSetting(next);
        return next;
      });
    }
    setValues(DEFAULT_VALUES);
  };

  const onChangeValues = (section, key, value) => {
    setValues((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const sectionHasValue = (section) =>
    Object.entries(section || {}).some(
      ([key, val]) =>
        !key.startsWith("is") &&
        val !== "" &&
        val !== null &&
        val !== undefined,
    );

  const getActiveSections = () => {
    const base = ["limitSetting", "marketSetting", "sessionSetting"];
    if (playType === "cricket") return [...base, "cricketBookmakingSetting"];
    if (playType === "soccer") return [...base, "soccerBookmakingSetting"];
    if (playType === "tennis") return [...base, "tennisBookmakingSetting"];
    return [
      ...base,
      "cricketBookmakingSetting",
      "soccerBookmakingSetting",
      "tennisBookmakingSetting",
    ];
  };

  const isValid = () => {
    if (
      settingValues.settingTypeFirst !== "default" &&
      isEmpty(settingValues.settingTypeFirstId)
    ) {
      toast.error(`Please Select ${startCase(select1)} wise`);
      return false;
    }
    if (
      settingValues.settingTypeSecond !== "default" &&
      isEmpty(settingValues.settingTypeSecondId)
    ) {
      toast.error(`Please Select ${startCase(select2)} wise`);
      return false;
    }
    const active = getActiveSections();
    if (!active.some((section) => sectionHasValue(values[section]))) {
      toast.error("Please enter some input to fields");
      return false;
    }
    return true;
  };

  const onSubmit = async () => {
    if (!isValid()) return;

    const active = getActiveSections();
    const payloadArray = active.reduce((acc, section) => {
      acc[section] = values[section];
      return acc;
    }, {});

    const { status, data: response } = await apiPost(apiPath.submitSetting, {
      ...settingValues,
      array: payloadArray,
    });
    if (status === 200 && response.success) {
      getDataSetting();
    }
  };

  const reset = () => {
    setSelect1("default");
    setSelect2("default");
    setTypeSelect(null);
    setTypeSelect2(null);
    setValues(DEFAULT_VALUES);
    setPlayType("all");
    const cleared = {
      settingTypeFirst: "default",
      settingTypeFirstId: "",
      settingTypeSecond: "default",
      settingTypeSecondId: "",
    };
    setSettingValues(cleared);
    handleChangeSetting(cleared);
  };

  const renderSectionGrid = (sectionKey, fields) => (
    <div className="grid">
      {fields.map((f) => {
        const checkboxKey = checkboxKeyFor(f.key);
        return (
          <div key={f.key} className="input-group">
            <label htmlFor={f.key}>{f.label}</label>
            <input
              autoComplete="off"
              type="number"
              name="value"
              placeholder={f.placeholder}
              value={values?.[sectionKey]?.[f.key] ?? ""}
              onChange={(e) =>
                onChangeValues(sectionKey, f.key, e.target.value)
              }
            />
            <input
              type="checkbox"
              tabIndex={-1}
              checked={!!values?.[sectionKey]?.[checkboxKey]}
              onChange={(e) =>
                onChangeValues(sectionKey, checkboxKey, e.target.checked)
              }
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <app-settings _nghost-serverapp-c134 className="ng-star-inserted">
      <div _ngcontent-serverapp-c134 className="full-wrap">
        <form
          _ngcontent-serverapp-c134
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div _ngcontent-serverapp-c134 className="main_wrap">
            <h2 _ngcontent-serverapp-c134>Settings</h2>

            <div _ngcontent-serverapp-c134 className="function-wrap funwrap">
              <ul _ngcontent-serverapp-c134 className="input-list diaplayflex">
                <li _ngcontent-serverapp-c134>
                  {TYPE_WISE_OPTIONS.map((opt) => (
                    <React.Fragment key={opt.id}>
                      <input
                        _ngcontent-serverapp-c134
                        autoComplete="off"
                        type="radio"
                        name="typeWise"
                        id={opt.id}
                        checked={select1 === opt.key}
                        onChange={() => handleChange1(opt.key)}
                      />
                      <label _ngcontent-serverapp-c134 htmlFor={opt.id}>
                        {opt.label}
                      </label>
                    </React.Fragment>
                  ))}
                </li>
              </ul>

              <ul _ngcontent-serverapp-c134 className="input-list diaplayflex">
                <li _ngcontent-serverapp-c134>
                  <input
                    _ngcontent-serverapp-c134
                    autoComplete="off"
                    type="radio"
                    name="usersWise"
                    id="usersWise"
                    checked={select2 === "default"}
                    onChange={() => handleChange2("default")}
                  />
                  <label _ngcontent-serverapp-c134 htmlFor="usersWise">
                    Default
                  </label>

                  {USER_WISE_OPTIONS.map((opt) => (
                    <React.Fragment key={opt.id}>
                      <input
                        _ngcontent-serverapp-c134
                        autoComplete="off"
                        type="radio"
                        name="usersWise"
                        id={opt.id}
                        className="ng-star-inserted"
                        checked={select2 === opt.key}
                        onChange={() => handleChange2(opt.key)}
                      />
                      <label
                        _ngcontent-serverapp-c134
                        htmlFor={opt.id}
                        className="ng-star-inserted"
                      >
                        {opt.label}
                      </label>
                    </React.Fragment>
                  ))}
                </li>
              </ul>

              <ul _ngcontent-serverapp-c134 className="input-list">
                <li _ngcontent-serverapp-c134>
                  {select2 !== "default" && (
                    <Select
                      className="user-wise"
                      classNamePrefix="ng"
                      placeholder={`Select ${startCase(select2)}`}
                      isClearable
                      isSearchable
                      options={list}
                      value={typeSelect}
                      onChange={(e) => {
                        setTypeSelect(e);
                        setSettingValues((prev) => {
                          const next = {
                            ...prev,
                            settingTypeSecondId: e?.value,
                          };
                          handleChangeSetting(next);
                          return next;
                        });
                      }}
                    />
                  )}
                  {select1 !== "default" && (
                    <Select
                      className="user-wise"
                      classNamePrefix="ng"
                      placeholder={`Select ${startCase(select1)}`}
                      isClearable
                      isSearchable
                      options={ObjSelect[select1]}
                      value={typeSelect2}
                      onChange={(e) => handleChangeTypeSelect2(e, select1)}
                    />
                  )}
                </li>
              </ul>
            </div>

            <div
              style={isMobile ? { maxWidth: "1200px" } : {}}
              _ngcontent-serverapp-c134
              className="container settings-container"
            >
              <div _ngcontent-serverapp-c134 className="ng-star-inserted">
                <h2 _ngcontent-serverapp-c134>Limit Setting&nbsp;</h2>
                {renderSectionGrid("limitSetting", limitSettingFields)}
              </div>

              <div _ngcontent-serverapp-c134 className="ng-star-inserted">
                <h2 _ngcontent-serverapp-c134>Market Setting &nbsp;</h2>
                {renderSectionGrid("marketSetting", marketSettingFields)}
              </div>

              <div _ngcontent-serverapp-c134 className="ng-star-inserted">
                <h2 _ngcontent-serverapp-c134>Session Setting &nbsp;</h2>
                {renderSectionGrid("sessionSetting", sessionSettingFields)}
              </div>
            </div>

            <div
              style={isMobile ? { maxWidth: "1200px" } : {}}
              _ngcontent-serverapp-c134
              className="container settings-container"
            >
              {(playType === "all" || playType === "cricket") && (
                <div _ngcontent-serverapp-c134 className="ng-star-inserted">
                  <h2 _ngcontent-serverapp-c134>
                    Cricket Bookmaking Setting &nbsp;
                  </h2>
                  {renderSectionGrid(
                    "cricketBookmakingSetting",
                    bookmakingSettingFields,
                  )}
                </div>
              )}

              {(playType === "all" || playType === "soccer") && (
                <div _ngcontent-serverapp-c134 className="ng-star-inserted">
                  <h2 _ngcontent-serverapp-c134>
                    Soccer Bookmaking Setting &nbsp;
                  </h2>
                  {renderSectionGrid(
                    "soccerBookmakingSetting",
                    bookmakingSettingFields,
                  )}
                </div>
              )}

              {(playType === "all" || playType === "tennis") && (
                <div _ngcontent-serverapp-c134 className="ng-star-inserted">
                  <h2 _ngcontent-serverapp-c134>
                    Tennis Bookmaking Setting &nbsp;
                  </h2>
                  {renderSectionGrid(
                    "tennisBookmakingSetting",
                    bookmakingSettingFields,
                  )}
                </div>
              )}
            </div>

            <div _ngcontent-serverapp-c134 className="btn_box">
              <button
                style={{ margin: "0" }}
                _ngcontent-serverapp-c134
                type="submit"
                className="btn-send"
              >
                Save
              </button>
              <button
                style={{ marginLeft: "5px" }}
                _ngcontent-serverapp-c134
                type="button"
                className="btn"
                onClick={reset}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </app-settings>
  );
};

export default Setting;
